import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, X, ArrowRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";

const statusOptions = [
  { value: "ALL", label: "All" },
  { value: "SUCCESS", label: "Success" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

export const EmailFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentStatus = searchParams.get("status") ?? "ALL";
  const initialDateRange = useMemo(() => {
    return {
      start: searchParams.get("startDate")
        ? new Date(searchParams.get("startDate")!)
        : null,
      end: searchParams.get("endDate")
        ? new Date(searchParams.get("endDate")!)
        : null,
    };
  }, [searchParams]);

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>(initialDateRange);

  const updateParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", "0");

    Object.entries(updates).forEach(([key, value]) => {
      if (!value) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams);
  };

  const handleStatusChange = (value: string) => {
    updateParams({
      status: value === "ALL" ? null : value,
    });
  };

  const handleDateChange = (range?: { from?: Date; to?: Date }) => {
    const start = range?.from || null;
    const end = range?.to || null;

    setDateRange({ start, end });

    if (start && end) {
      const endDate = new Date(end);
      endDate.setHours(23, 59, 59, 999);
      updateParams({
        startDate: start.toISOString(),
        endDate: endDate.toISOString(),
      });
    } else {
      updateParams({
        startDate: null,
        endDate: null,
      });
    }
  };

  const handleClearDateRange = () => {
    setDateRange({ start: null, end: null });
    updateParams({
      startDate: null,
      endDate: null,
    });
  };

  return (
    <div className="flex gap-3 items-center flex-wrap">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`w-48 justify-start text-left font-normal ${
              !dateRange.start && "text-muted-foreground"
            }`}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange.start && dateRange.end
              ? `${format(dateRange.start, "LLL dd, y")} - ${format(
                  dateRange.end,
                  "LLL dd, y",
                )}`
              : "Pick date range"}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-4" align="start">
          <div className="flex items-end gap-3 mb-4 px-4">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1.5 block">
                Start
              </label>
              <div className="h-10 px-3 py-2 border rounded-md text-sm flex items-center">
                {dateRange.start
                  ? format(dateRange.start, "LLL dd, y")
                  : "Select"}
              </div>
            </div>

            <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />

            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1.5 block">
                End
              </label>
              <div className="h-10 px-3 py-2 border rounded-md text-sm flex items-center">
                {dateRange.end ? format(dateRange.end, "LLL dd, y") : "Select"}
              </div>
            </div>

            {(dateRange.start || dateRange.end) && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={handleClearDateRange}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <Calendar
            mode="range"
            selected={{
              from: dateRange.start || undefined,
              to: dateRange.end || undefined,
            }}
            onSelect={handleDateChange}
            numberOfMonths={2}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <Select value={currentStatus} onValueChange={handleStatusChange}>
        <SelectTrigger className="w-36">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          {statusOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
